/**
 * Display formatting.
 *
 * Centralised because money and dates are where inconsistency is most visible
 * and least forgivable: `$1,240.00` on one screen and `1240` on the next reads
 * as two different products bolted together.
 */

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

/**
 * Format a money value that came from the API.
 *
 * ── THE RULE THIS ENCODES ───────────────────────────────────────────────────
 * Totals are computed SERVER-SIDE (Phase 1 §3.2) and the UI displays what it
 * was given. This function formats; it never sums, never recomputes, and never
 * "fixes" a value that looks wrong. If a displayed total disagrees with the
 * line items, that is a bug to surface — papering over it here would hide the
 * one discrepancy the verification suite is built to catch.
 *
 * PostgREST returns numerics as strings, so the coercion is deliberate.
 *
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export function money (value) {
  const n = Number(value)
  return currency.format(Number.isFinite(n) ? n : 0)
}

const dateFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const dateTimeFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
})
const timeFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })
const dateOnlyRe = /^(\d{4})-(\d{2})-(\d{2})$/

function dateForDisplay (value) {
  const match = typeof value === 'string' ? dateOnlyRe.exec(value) : null
  if (!match) return new Date(value)

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const d = new Date(year, month - 1, day)
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    return new Date(Number.NaN)
  }
  return d
}

/** @param {string|null|undefined} iso */
export function formatDate (iso) {
  if (!iso) return '—'
  const d = dateForDisplay(iso)
  return Number.isNaN(d.getTime()) ? '—' : dateFmt.format(d)
}

/** @param {string|null|undefined} iso */
export function formatDateTime (iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : dateTimeFmt.format(d)
}

/** @param {string|null|undefined} iso */
export function formatTime (iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : timeFmt.format(d)
}

/**
 * Relative age, for timelines. Falls back to an absolute date past a week,
 * because "23 days ago" is harder to act on than a date.
 * @param {string|null|undefined} iso
 */
export function timeAgo (iso) {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'

  const seconds = Math.round((Date.now() - then) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days <= 7) return `${days}d ago`
  return formatDate(iso)
}

/** An `<input type="datetime-local">` value from an ISO string. */
export function toLocalInput (iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** An ISO string from an `<input type="datetime-local">` value. */
export function fromLocalInput (value) {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** A `YYYY-MM-DD` date input value, offset by days from today. */
export function dateInputValue (offsetDays = 0) {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Sentence-case a snake_case enum for display: `on_hold` → `On hold`. */
export function humanise (value) {
  if (!value) return ''
  const s = String(value).replace(/_/g, ' ')
  return s[0].toUpperCase() + s.slice(1)
}
